// =========================================================================
// HelpyDoc Metabolic · Pantallas de Autenticación
// Diseño idéntico al piloto original (app-movil.html)
// Lógica: Firebase Auth real + fallback mock
// =========================================================================
import { useState, useEffect } from 'react';
import { Eye, EyeOff, FileText, Lock, Stethoscope, type LucideIcon } from 'lucide-react';
import { ICONOS_CATEGORIA, ICONOS_ESTADO, ICONOS_NAVEGACION } from '@/config/iconografia';
import { AuthService, cuentaPrincipalDeLocal } from '@/services/AuthService';
import type { Session } from '@/services/AuthService';
import { isFirebaseEnabled, firebaseSignIn, firebaseSignUp, firebaseSignInWithGoogle, firebaseSignInWithApple } from '@/services/firebase';
import { ApiClient } from '@/services/api-client';
import { getDataMode } from '@/config/data-mode';
import { DataService, type UserGoal, concederConsentimientosRequeridos } from '@/services';
import { GoalSelector } from '@/components';
import { LegalModal } from '@/components/LegalModal';
import { PerfilClinicoInicial, type PerfilInicial } from '@/components/PerfilClinicoInicial';
import { registrarMedida } from '@/features/progreso/almacen';
import {
  leerBorradorOnboarding,
  guardarBorradorOnboarding,
  limpiarBorradorOnboarding,
} from '@/lib/onboarding-borrador';
import type { LegalDoc } from '@/legal/legal-content';

// ── Props por tipo de pantalla ────────────────────────────────────────────
interface SplashProps    { type: 'splash';    onDone: () => void; }
interface LoginProps     { type: 'login';     onLogin: (s: Session) => void; onGoRegister: () => void; }
interface RegisterProps  { type: 'register';  onRegister: (s: Session) => void; onGoLogin: () => void; }
interface OnboardingProps { type: 'onboarding'; session: Session; onDone: () => void; }
interface ConsentsProps  { type: 'consents';  onDone: () => void; onLogout: () => void; }

export type AuthScreensProps = SplashProps | LoginProps | RegisterProps | OnboardingProps | ConsentsProps;

// ── Logo HelpyDoc ─────────────────────────────────────────────────────────
function HDLogo({ size = 48 }: { size?: number }) {
  return (
    <img
      src="helpydoc-logo-alpha.png"
      alt="HelpyDoc"
      width={size}
      height={size}
      style={{ objectFit: 'contain', display: 'block' }}
    />
  );
}

// ── Splash ────────────────────────────────────────────────────────────────
function SplashScreen({ onDone }: { onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 1800);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(160deg, #1A2F6E 0%, #2B8DD6 55%, #3A9E4A 100%)',
      gap: 20,
    }}>
      <div style={{
        animation: 'hdPulse 1.2s ease-in-out infinite',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <img
          src="helpydoc-logo-alpha.png"
          alt="HelpyDoc"
          style={{
            width: 140, height: 140, objectFit: 'contain', display: 'block',
            filter: 'drop-shadow(0 8px 24px rgba(0,0,0,.4)) brightness(0) invert(1)',
          }}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <div style={{ color: '#fff', fontSize: 28, fontWeight: 800, letterSpacing: 0.5 }}>HelpyDoc</div>
        <div style={{ color: 'rgba(255,255,255,.85)', fontSize: 14, fontWeight: 600, letterSpacing: 3, textTransform: 'uppercase' }}>Metabolic</div>
      </div>
      <div style={{ color: 'rgba(255,255,255,.55)', fontSize: 12, fontWeight: 400, marginTop: 2 }}>
        Nutrición inteligente · v1.1
      </div>
      <style>{`
        @keyframes hdPulse {
          0%,100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }
      `}</style>
    </div>
  );
}

// ── Login ─────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin, onGoRegister }: Omit<LoginProps, 'type'>) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  // `null` fuera de desarrollo, que es lo que hace desaparecer el aviso de
  // abajo en producción. No es estado: no cambia mientras la pantalla vive.
  const cuentaLocal = cuentaPrincipalDeLocal();

  const handleLogin = async () => {
    setError('');
    setLoading(true);
    await new Promise(r => setTimeout(r, 700));
    const result = isFirebaseEnabled
      ? await firebaseSignIn(email, password)
      : AuthService.login(email, password);
    setLoading(false);
    if (result.ok && result.session) onLogin(result.session);
    else setError(result.error ?? 'Error al iniciar sesión');
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    const result = await firebaseSignInWithGoogle();
    setLoading(false);
    if (result.ok && result.session) onLogin(result.session);
    else setError(result.error ?? 'No se pudo iniciar sesión con Google');
  };

  const handleApple = async () => {
    setError('');
    setLoading(true);
    const result = await firebaseSignInWithApple();
    setLoading(false);
    if (result.ok && result.session) onLogin(result.session);
    else setError(result.error ?? 'No se pudo iniciar sesión con Apple');
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      {/* Header dark-blue — idéntico al piloto */}
      <div style={{
        background: 'linear-gradient(160deg, #1A2F6E 0%, #2B8DD6 100%)',
        padding: '48px 28px 36px', borderRadius: '0 0 36px 36px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        flexShrink: 0,
      }}>
        <div style={{
          background: '#fff', borderRadius: 20, padding: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,.15)',
        }}>
          <HDLogo size={72} />
        </div>
        <div style={{ color: '#fff', fontSize: 22, fontWeight: 800 }}>Bienvenido/a</div>
        <div style={{ color: 'rgba(255,255,255,.75)', fontSize: 13, textAlign: 'center' }}>
          Inicia sesión en tu cuenta HelpyDoc Metabolic
        </div>
      </div>

      <div style={{ flex: 1, padding: '28px 24px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Email */}
          <div>
            <label style={{
              fontSize: 12, fontWeight: 700, color: 'var(--ink-500)',
              textTransform: 'uppercase', letterSpacing: '.06em',
              display: 'block', marginBottom: 6,
            }}>Correo electrónico</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="tu@email.com"
              style={inputStyle}
            />
          </div>

          {/* Contraseña */}
          <div>
            <label style={{
              fontSize: 12, fontWeight: 700, color: 'var(--ink-500)',
              textTransform: 'uppercase', letterSpacing: '.06em',
              display: 'block', marginBottom: 6,
            }}>Contraseña</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                style={{ ...inputStyle, paddingRight: 46 }}
              />
              <button
                onClick={() => setShowPass(p => !p)}
                style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: 'var(--ink-300)', cursor: 'pointer',
                }}
                aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                {showPass ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
              </button>
            </div>
            <div style={{ textAlign: 'right', marginTop: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--green-text)', fontWeight: 600, cursor: 'pointer' }}>
                ¿Olvidaste tu contraseña?
              </span>
            </div>
          </div>

          {error && (
            <div style={{
              background: '#FEF2F2', border: '1px solid #FECACA',
              borderRadius: 10, padding: '10px 14px',
              fontSize: 13, color: '#DC2626', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6,
            }}><ICONOS_ESTADO.advertencia size={15} style={{ flexShrink: 0 }} aria-hidden="true" /><span>{error}</span></div>
          )}

          <button
            onClick={handleLogin}
            disabled={loading}
            style={{
              width: '100%', padding: 15, borderRadius: 14,
              background: loading ? 'var(--ink-100)' : 'var(--green-grad)',
              color: loading ? 'var(--ink-300)' : '#fff',
              fontSize: 15, fontWeight: 800,
              boxShadow: loading ? 'none' : '0 6px 20px rgba(8,193,134,.4)',
              cursor: loading ? 'default' : 'pointer',
              transition: 'all .2s', border: 'none',
            }}>
            {loading ? 'Entrando…' : 'Iniciar sesión'}
          </button>

          {/* Aviso del acceso local. Se pinta solo si la excepción de
              desarrollo está REALMENTE activa: se pregunta por ella en vez de
              darla por hecha, y el correo sale de la misma fuente que decide.
              Antes era un cartel fijo y llegó al build de producción, donde
              decía a un paciente que entrase con la cuenta de administración
              —falso— y publicaba esa dirección en la pantalla de acceso. */}
          {cuentaLocal && (
            <div style={{
              // Mismo caso que el botón de Google: fondo fijo → texto fijo.
              background: '#F0FDF9', borderRadius: 10, padding: '10px 14px',
              fontSize: 12, color: '#2A2E37', textAlign: 'center', lineHeight: 1.6,
            }}>
              <ICONOS_CATEGORIA.consejos size={13} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} aria-hidden="true" />
              <b>En local:</b> entra con <b>{cuentaLocal}</b> y cualquier
              contraseña de 6+ caracteres. Con otro correo, crea antes la cuenta.
            </div>
          )}
        </div>

        {/* Separador */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--ink-100)' }} />
          <span style={{ fontSize: 12, color: 'var(--ink-300)', fontWeight: 600 }}>o</span>
          <div style={{ flex: 1, height: 1, background: 'var(--ink-100)' }} />
        </div>

        {/* Continuar con Google */}
        <button
          onClick={handleGoogle}
          disabled={loading}
          style={{
            width: '100%', padding: 13, borderRadius: 14, marginBottom: 20,
            // Fondo blanco fijo (lo exige la identidad de Google), así que el
            // texto TAMBIÉN va fijo. Con `var(--ink-700)` el texto se aclaraba
            // en modo oscuro y quedaba a 1,46:1 sobre blanco: ilegible.
            background: '#fff', color: '#2A2E37',
            border: '1px solid var(--ink-100)', cursor: loading ? 'default' : 'pointer',
            fontSize: 15, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            boxShadow: '0 1px 3px rgba(0,0,0,.06)',
          }}>
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.3-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 43.5c5.4 0 10.3-2 14-5.3l-6.5-5.3C29.6 34.5 26.9 35.5 24 35.5c-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.6 38.9 16.2 43.5 24 43.5z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l6.5 5.3c-.5.4 7-5.1 7-14.2 0-1.2-.1-2.3-.3-3.5z"/>
          </svg>
          Continuar con Google
        </button>

        {/* Continuar con Apple */}
        <button
          onClick={handleApple}
          disabled={loading}
          style={{
            width: '100%', padding: 13, borderRadius: 14, marginBottom: 20,
            background: '#000', color: '#fff', border: 'none',
            cursor: loading ? 'default' : 'pointer', fontSize: 15, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
            <path d="M16.37 12.7c-.02-2.06 1.68-3.05 1.76-3.1-0.96-1.4-2.46-1.6-2.99-1.62-1.27-.13-2.48.75-3.13.75-.64 0-1.64-.73-2.7-.71-1.39.02-2.67.81-3.38 2.05-1.44 2.5-.37 6.2 1.04 8.23.69.99 1.51 2.1 2.58 2.06 1.04-.04 1.43-.67 2.69-.67 1.25 0 1.61.67 2.7.65 1.12-.02 1.83-1.01 2.51-2.01.79-1.15 1.12-2.27 1.13-2.33-.02-.01-2.17-.83-2.19-3.29zM14.3 6.84c.57-.69.96-1.65.85-2.61-.82.03-1.82.55-2.41 1.24-.53.61-.99 1.59-.87 2.53.92.07 1.86-.47 2.43-1.16z"/>
          </svg>
          Continuar con Apple
        </button>

        <div style={{ textAlign: 'center' }}>
          <span style={{ fontSize: 14, color: 'var(--ink-500)' }}>¿Primera vez aquí? </span>
          <span
            onClick={onGoRegister}
            style={{ fontSize: 14, color: 'var(--green-text)', fontWeight: 700, cursor: 'pointer' }}>
            Crear cuenta
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Registro ──────────────────────────────────────────────────────────────
function RegisterScreen({ onRegister, onGoLogin }: Omit<RegisterProps, 'type'>) {
  const [nombre, setNombre]     = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleRegister = async () => {
    setError('');
    setLoading(true);
    await new Promise(r => setTimeout(r, 700));
    const result = isFirebaseEnabled
      ? await firebaseSignUp(email, password, nombre)
      : AuthService.register(email, password, nombre);
    setLoading(false);
    if (result.ok && result.session) onRegister(result.session);
    else setError(result.error ?? 'Error al registrar');
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      {/* Header dark-blue — idéntico al piloto */}
      <div style={{
        background: 'linear-gradient(160deg, #1A2F6E 0%, #2B8DD6 100%)',
        padding: '40px 28px 32px', borderRadius: '0 0 36px 36px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
        flexShrink: 0,
      }}>
        <div style={{
          background: '#fff', borderRadius: 16, padding: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,.15)',
        }}>
          <HDLogo size={56} />
        </div>
        <div style={{ color: '#fff', fontSize: 20, fontWeight: 800 }}>Crear cuenta</div>
        <div style={{ color: 'rgba(255,255,255,.75)', fontSize: 13, textAlign: 'center' }}>
          Empieza tu journey metabólico
        </div>
      </div>

      <div style={{ flex: 1, padding: '24px 24px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { label: 'Nombre', type: 'text', value: nombre, onChange: setNombre, placeholder: 'Tu nombre' },
            { label: 'Correo electrónico', type: 'email', value: email, onChange: setEmail, placeholder: 'tu@email.com' },
            { label: 'Contraseña', type: 'password', value: password, onChange: setPassword, placeholder: 'Mín. 6 caracteres' },
          ].map(({ label, type, value, onChange, placeholder }) => (
            <div key={label}>
              <label style={{
                fontSize: 12, fontWeight: 700, color: 'var(--ink-500)',
                textTransform: 'uppercase', letterSpacing: '.06em',
                display: 'block', marginBottom: 6,
              }}>{label}</label>
              <input
                type={type}
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                style={inputStyle}
              />
            </div>
          ))}

          {error && (
            <div style={{
              background: '#FEF2F2', border: '1px solid #FECACA',
              borderRadius: 10, padding: '10px 14px',
              fontSize: 13, color: '#DC2626', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6,
            }}><ICONOS_ESTADO.advertencia size={15} style={{ flexShrink: 0 }} aria-hidden="true" /><span>{error}</span></div>
          )}

          <button
            onClick={handleRegister}
            disabled={loading || !email || !password || !nombre}
            style={{
              width: '100%', padding: 15, borderRadius: 14,
              background: loading || !email || !password || !nombre ? 'var(--ink-100)' : 'var(--green-grad)',
              color: loading || !email || !password || !nombre ? 'var(--ink-300)' : '#fff',
              fontSize: 15, fontWeight: 800,
              boxShadow: loading ? 'none' : '0 6px 20px rgba(8,193,134,.4)',
              cursor: loading ? 'default' : 'pointer',
              border: 'none', transition: 'all .2s',
            }}>
            {loading ? 'Creando cuenta…' : 'Crear cuenta'}
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <span style={{ fontSize: 14, color: 'var(--ink-500)' }}>¿Ya tienes cuenta? </span>
          <span
            onClick={onGoLogin}
            style={{ fontSize: 14, color: 'var(--green-text)', fontWeight: 700, cursor: 'pointer' }}>
            Iniciar sesión
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Onboarding ────────────────────────────────────────────────────────────
function OnboardingScreen({ session, onDone }: Omit<OnboardingProps, 'type'>) {
  // F08 G3 · restaurar la posición y el objetivo del borrador (si se retomó el
  // wizard tras una interrupción); si no hay borrador, arranca de cero.
  const [step, setStep] = useState(() => leerBorradorOnboarding()?.step ?? 0);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [goal, setGoal] = useState<UserGoal>(() => leerBorradorOnboarding()?.goal ?? 'maintain');

  const steps: { Icon: LucideIcon; title: string; desc: string; goal?: boolean; perfil?: boolean }[] = [
    { Icon: ICONOS_NAVEGACION.platos, title: '¡Bienvenido/a!', desc: `Hola ${session.nombre}, tu app de nutrición metabólica está lista.` },
    { Icon: ICONOS_CATEGORIA.progreso, title: 'Registra tu progreso', desc: 'Añade tus comidas, estado emocional y actividad física cada día.' },
    { Icon: ICONOS_CATEGORIA.objetivos, title: '¿Cuál es tu objetivo?', desc: 'Ajustaremos tus metas y la estimación de calorías a lo que buscas.', goal: true },
    // Va el último a propósito: se piden datos de salud, y para entonces los
    // consentimientos ya están aceptados (la puerta de consentimiento corre
    // ANTES de este onboarding, en App.tsx).
    { Icon: ICONOS_CATEGORIA.progreso, title: 'Valoración inicial', desc: '', perfil: true },
  ];

  // Clamp defensivo: un borrador de una versión con más pantallas no debe
  // indexar fuera del array (steps[undefined] → pantalla en blanco).
  const stepSeguro = Math.min(Math.max(step, 0), steps.length - 1);
  const current = steps[stepSeguro];
  const isLastStep = stepSeguro === steps.length - 1;

  // F08 G3 · persistir el progreso (posición + objetivo) como borrador local en
  // cada cambio, para que una interrupción se pueda reanudar donde se dejó.
  useEffect(() => {
    guardarBorradorOnboarding({ step: stepSeguro, goal });
  }, [stepSeguro, goal]);

  // Guardar y continuar son dos cosas distintas: un caso bloqueado SÍ se
  // guarda —es lo que tiene que leer quien lo revise— y NO continúa.
  const finalizar = async (perfil?: PerfilInicial, continuar = true) => {
    setBootstrapping(true);
    try {
      if (getDataMode() === 'api' && ApiClient.isConfigured()) {
        await ApiClient.bootstrapUser({ nombre: session.nombre });
      }
      // Solo se escribe lo que la persona haya rellenado: un campo omitido se
      // queda ausente, nunca en cero.
      await DataService.actualizarUsuario({
        goal,
        // Sella el hito de UI que permite reanudar el onboarding (F08 G2): quien
        // cierre la app antes de llegar aquí no tendrá el flag y volverá al
        // wizard en el siguiente login, en vez de colarse en la app para siempre.
        onboardingCompletedAt: new Date().toISOString(),
        ...(perfil?.sexo ? { sexo: perfil.sexo } : {}),
        ...(perfil?.tallaCm ? { altura: perfil.tallaCm } : {}),
        ...(perfil?.pesoKg ? { pesoActual: perfil.pesoKg } : {}),
        ...(perfil?.comorbilidades?.length ? { comorbilidades: perfil.comorbilidades } : {}),
      });
      if (perfil?.pesoKg || perfil?.cinturaCm) {
        registrarMedida(
          { pesoKg: perfil.pesoKg, cinturaCm: perfil.cinturaCm },
          new Date().toISOString().slice(0, 10),
        );
      }
    } catch (err) {
      console.warn('[Onboarding] bootstrapUser falló, continuando:', err);
    } finally {
      setBootstrapping(false);
      if (continuar) {
        // Wizard completado: el borrador ya no sirve (el dato definitivo quedó
        // sellado en perfil/valoración). Un caso bloqueado (continuar=false) SÍ
        // conserva el borrador para poder reanudarlo.
        limpiarBorradorOnboarding();
        onDone();
      }
    }
  };

  const handleNext = async () => {
    if (!isLastStep) { setStep(s => s + 1); return; }
    await finalizar();
  };

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(160deg, #1A2F6E 0%, #2B8DD6 55%, #3A9E4A 100%)',
      padding: '0 32px',
    }}>
      <div style={{ textAlign: 'center', animation: 'hdFadeIn .4s ease both' }} key={step}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <current.Icon size={72} strokeWidth={1.5} color="#fff" aria-hidden="true" />
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#fff', marginBottom: 12 }}>{current.title}</div>
        {current.desc && (
          <div style={{ fontSize: 15, color: 'rgba(255,255,255,.85)', lineHeight: 1.5 }}>{current.desc}</div>
        )}
        {current.goal && (
          <div style={{ marginTop: 24, background: '#fff', borderRadius: 16, padding: 16, textAlign: 'left' }}>
            <GoalSelector value={goal} onChange={setGoal} />
          </div>
        )}
        {current.perfil && (
          <div style={{ marginTop: 16 }}>
            <PerfilClinicoInicial
              onGuardar={(p) => void finalizar(p, false)}
              onFin={onDone}
              guardando={bootstrapping}
            />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, marginTop: 48, marginBottom: 32 }}>
        {steps.map((_, i) => (
          <div key={i} style={{
            width: i === step ? 20 : 6, height: 6, borderRadius: 3,
            background: i === step ? '#fff' : 'rgba(255,255,255,.4)',
            transition: 'all .3s',
          }} />
        ))}
      </div>

      {/* El paso de valoración lleva sus propios botones (Continuar / Omitir):
          un "Siguiente" adicional daría dos salidas para la misma decisión. */}
      {!current.perfil && (
        <button
          onClick={handleNext}
          disabled={bootstrapping}
          style={{
            padding: '16px 48px', borderRadius: 14, fontSize: 16, fontWeight: 800,
            background: '#fff', color: 'var(--green-text)', border: 'none',
            boxShadow: '0 8px 24px rgba(0,0,0,.15)',
            opacity: bootstrapping ? 0.7 : 1, cursor: 'pointer',
          }}>
          {bootstrapping ? 'Configurando…' : 'Siguiente'}
        </button>
      )}

      <style>{`
        @keyframes hdFadeIn { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:none; } }
      `}</style>
    </div>
  );
}

// ── Pantalla de consentimiento (RGPD) ──────────────────────────────────────
function ConsentScreen({ onDone, onLogout }: Omit<ConsentsProps, 'type'>) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [legalDoc, setLegalDoc] = useState<LegalDoc['id'] | null>(null);

  const items: { Icon: LucideIcon; title: string; desc: string }[] = [
    { Icon: Stethoscope, title: 'Procesar datos de salud', desc: 'Para registrar tus comidas y analizar su composición nutricional.' },
    { Icon: ICONOS_NAVEGACION.registrar, title: 'Almacenar las fotos de tus platos', desc: 'Se suben de forma segura para que la IA las analice.' },
    { Icon: FileText, title: 'Términos del servicio y Política de privacidad', desc: 'Tratamiento de tus datos conforme al RGPD.' },
  ];

  const handleAccept = async () => {
    setSaving(true);
    setError(null);
    try {
      await concederConsentimientosRequeridos();
      onDone();
    } catch {
      setError('No se pudo guardar tu consentimiento. Revisa tu conexión e inténtalo de nuevo.');
      setSaving(false);
    }
  };

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(160deg, #1A2F6E 0%, #2B8DD6 55%, #3A9E4A 100%)',
      padding: '0 28px', overflowY: 'auto',
    }}>
      <div style={{ textAlign: 'center', maxWidth: 460, width: '100%', animation: 'hdFadeIn .4s ease both' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <Lock size={56} strokeWidth={1.5} color="#fff" aria-hidden="true" />
        </div>
        <div style={{ fontSize: 23, fontWeight: 800, color: '#fff', marginBottom: 8 }}>Tu privacidad</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,.85)', lineHeight: 1.5, marginBottom: 20 }}>
          HelpyDoc es una app de salud. Para usarla necesitamos tu consentimiento explícito para:
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: 8, textAlign: 'left' }}>
          {items.map((it, i) => (
            <div key={i} style={{
              display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 12px',
              borderTop: i === 0 ? 'none' : '1px solid var(--ink-50)',
            }}>
              <it.Icon size={22} strokeWidth={2} color="var(--green-deep, #3A9E4A)" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>{it.title}</div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-400)', lineHeight: 1.4 }}>{it.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6, fontSize: 12.5 }}>
          {([
            ['terms', 'Términos'],
            ['privacy', 'Política de privacidad'],
            ['disclaimer', 'Aviso médico'],
          ] as [LegalDoc['id'], string][]).map(([id, label], i) => (
            <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {i > 0 && <span style={{ color: 'rgba(255,255,255,.5)' }}>·</span>}
              <button
                type="button"
                onClick={() => setLegalDoc(id)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#fff', textDecoration: 'underline', fontSize: 12.5, fontWeight: 600 }}
              >{label}</button>
            </span>
          ))}
        </div>

        {error && (
          <div style={{ marginTop: 14, color: '#fff', background: 'rgba(214,40,40,.35)', border: '1px solid rgba(255,255,255,.4)', borderRadius: 12, padding: '10px 12px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <ICONOS_ESTADO.advertencia size={15} style={{ flexShrink: 0 }} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <button
          onClick={handleAccept}
          disabled={saving}
          style={{
            marginTop: 20, width: '100%', padding: '15px 24px', borderRadius: 14, fontSize: 16, fontWeight: 800,
            background: '#fff', color: 'var(--green-text)', border: 'none',
            boxShadow: '0 8px 24px rgba(0,0,0,.15)', opacity: saving ? 0.7 : 1, cursor: 'pointer',
          }}>
          {saving ? 'Guardando…' : 'Acepto y continúo'}
        </button>

        <button
          onClick={onLogout}
          disabled={saving}
          style={{
            marginTop: 10, marginBottom: 24, width: '100%', padding: '12px', borderRadius: 14, fontSize: 14, fontWeight: 600,
            background: 'transparent', color: 'rgba(255,255,255,.85)', border: '1px solid rgba(255,255,255,.4)',
            cursor: 'pointer',
          }}>
          Ahora no (cerrar sesión)
        </button>

        <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.7)', lineHeight: 1.5, marginBottom: 24 }}>
          Puedes revocar tu consentimiento cuando quieras desde Ajustes. Sin estos permisos la app no puede registrar ni analizar tus comidas.
        </div>
      </div>

      <style>{`@keyframes hdFadeIn { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:none; } }`}</style>

      <LegalModal docId={legalDoc} onClose={() => setLegalDoc(null)} />
    </div>
  );
}

// ── Estilo de input compartido ─────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '13px 16px', borderRadius: 12,
  border: '1.5px solid var(--ink-100)', fontSize: 15,
  fontFamily: '"Hanken Grotesk", ui-sans-serif, system-ui, sans-serif',
  background: 'var(--paper)', color: 'var(--navy)',
  outline: 'none', boxSizing: 'border-box',
};

// ── Dispatcher ────────────────────────────────────────────────────────────
export function AuthScreens(props: AuthScreensProps) {
  if (props.type === 'splash')     return <SplashScreen onDone={props.onDone} />;
  if (props.type === 'login')      return <LoginScreen onLogin={props.onLogin} onGoRegister={props.onGoRegister} />;
  if (props.type === 'register')   return <RegisterScreen onRegister={props.onRegister} onGoLogin={props.onGoLogin} />;
  if (props.type === 'onboarding') return <OnboardingScreen session={props.session} onDone={props.onDone} />;
  if (props.type === 'consents')   return <ConsentScreen onDone={props.onDone} onLogout={props.onLogout} />;
  return null;
}
